<template>
  <div>
    <!-- Header -->
    <div class="py-3 px-3 text-start style-modal-header">
      <span class="scss-title">Select Country</span>
      <i
        class="bi bi-x style-close-icon scss-click-effect"
        data-bs-dismiss="modal"
        aria-label="Close"
      ></i>
    </div>

    <!-- Search Bar -->
    <div class="style-search-bar-background mt-1 p-2">
      <input
        type="text"
        class="style-search-bar scss-shadow-container"
        placeholder="Search for a country"
        v-model="searchValue"
      />

      <i class="bi bi-search style-search-icon"></i>
    </div>

    <!-- Country Code Options -->
    <div class="style-option-container">
      <div v-if="populatedCountries.length !== 0">
        <div class="d-flex flex-row">
          <div class="flex-grow-1">
            <!-- Popular Location -->
            <div
              class="style-country-code"
              v-if="searchValue === null || searchValue === ''"
            >
              <!-- Header -->
              <div class="style-prefix py-2 ps-3 text-start">
                Popular Location
              </div>
              <!-- Options -->
              <div v-for="countryCode in popularLocations" :key="countryCode.code">
                <div
                  :class="[
                    {
                      'style-item-selected': isCountryPhoneSelected(
                        countryCode.phone
                      ),
                    },
                    'style-country-code-container d-flex flex-row scss-click-effect',
                  ]"
                  @click="
                    $emit(
                      'update:selectedCountryCode',
                      countryCode.phone.toString()
                    )
                  "
                  data-bs-dismiss="modal"
                >
                  <div class="ps-3 mb-1">
                    <country-flag :country="countryCode.code" />
                  </div>

                  <div class="ps-4 scss-medium-font text-start">
                    {{ formatCountryCode(countryCode.name, countryCode.phone) }}
                  </div>
                </div>
              </div>
            </div>
            <!-- Country List -->
            <div v-for="prefix in countryPrefixes" :key="prefix">
              <div class="style-country-code">
                <!-- Header -->
                <div :id="`countryStartWith${prefix}`">
                  <div class="style-prefix py-2 ps-3 text-start">
                    {{ prefix }}
                  </div>
                </div>
                <!-- Options -->
                <div
                  v-for="countryCode in populatedCountries[prefix].sort(
                    sortCountryList
                  )"
                  :key="countryCode.code"
                >
                  <div
                    :class="[
                      {
                        'style-item-selected': isCountryPhoneSelected(
                          countryCode.phone
                        ),
                      },
                      'style-country-code-container d-flex flex-row scss-click-effect',
                    ]"
                    @click="
                      $emit(
                        'update:selectedCountryCode',
                        countryCode.phone.toString()
                      )
                    "
                    data-bs-dismiss="modal"
                  >
                    <div class="ps-3 mb-1">
                      <country-flag :country="countryCode.code" />
                    </div>

                    <div class="ps-4 scss-medium-font text-start">
                      {{
                        formatCountryCode(countryCode.name, countryCode.phone)
                      }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Shortcut navigation -->
          <div
            class="style-shortcut-navigation scss-clickable justify-content-center"
          >
            <div v-for="prefix in countryPrefixes" :key="prefix">
              <a :href="`#countryStartWith${prefix}`">{{ prefix }}</a>
            </div>
          </div>
        </div>
      </div>

      <!-- If no search result found -->
      <div v-if="populatedCountries.length === 0">
        <item-not-found message="No result found." />
      </div>
    </div>
  </div>
</template>

<script>
import { countries } from "@/common/constant/Countries.js";
import ItemNotFound from "../ItemNotFound.vue";

export default {
  name: "CountryCodeInput",
  components: { ItemNotFound },
  props: { selectedCountryCode: String },
  data() {
    return {
      searchValue: null,
      countries,
    };
  },
  methods: {
    formatCountryCode(countryCode, phoneCode) {
      return `${countryCode} (+${phoneCode})`;
    },
    sortCountryList(country1, country2) {
      if (country1.name < country2.name) {
        return -1;
      }

      if (country1.name > country2.name) {
        return 1;
      }

      return 0;
    },
  },
  computed: {
    populatedCountries() {
      let data;

      // Check if any search value entered
      if (this.searchValue === null) {
        // If there is no search value
        // Return original country list
        data = this.countries;
      } else {
        const searchValue = this.searchValue
          .replace(/[.*+?^${}()|[\]\\]/g, "")
          .toLocaleUpperCase();
        // Filter country list
        // Find match result
        data = this.countries.filter(
          (country) =>
            country.name.toLocaleUpperCase().search(searchValue) >= 0 ||
            country.phone.toString().search(searchValue) >= 0
        );
      }

      // List is empty (Not match search result)
      if (data.length === 0) {
        return data;
      }

      // Populate country code
      // Categories country alphabetically
      const list = data.reduce((accumulatedObject, country) => {
        let firstLetter = country.name[0].toLocaleUpperCase();

        // Check if properties exists
        if (Object.hasOwn(accumulatedObject, firstLetter)) {
          accumulatedObject[firstLetter].push(country);
        } else {
          // Define new properties if properties don't exist
          accumulatedObject[firstLetter.toString()] = [];
          accumulatedObject[firstLetter].push(country);
        }

        return accumulatedObject;
      }, {});

      return list;
    },
    popularLocations() {
      return this.countries.filter((country) => country.popular === true);
    },
    countryPrefixes() {
      return Object.keys(this.populatedCountries).sort();
    },
    isCountryPhoneSelected() {
      return (countryPhone) =>
        countryPhone.toString() === this.selectedCountryCode.toString();
    },
  },
};
</script>

<style lang="scss" scoped>
// Search Bar
.style-search-bar-background {
  position: relative;
}

.style-search-bar {
  width: 100%;
  height: 40px;
  border: none;
  padding-left: 40px;
}

input:focus {
  outline: none;
}

input[type="text"]::-webkit-input-placeholder {
  color: rgba(78, 79, 88, 0.25);
}

.style-search-icon {
  color: rgba(78, 79, 88, 0.25);
  position: absolute;
  top: 50%;
  left: 25px;
  transform: translate(-50%, -50%);
}

.style-country-code {
  width: 98%;
}

.style-shortcut-navigation {
  display: flex;
  flex-direction: column;
  font-size: 10.25px;
  color: $text-blue;
  height: 65vh;
  position: -webkit-sticky; /* Safari */
  position: sticky;
  top: 0;
  right: 5px;
  width: 5%;
  max-width: 10px;
}

.style-shortcut-navigation > div > a {
  text-decoration: none;
}

.style-option-container {
  height: 80vh;
  overflow: scroll;
  -ms-overflow-style: none;
  scrollbar-width: none; /* Firefox */
}

/* Hide scrollbar for Chrome, Safari and Opera */
.style-option-container::-webkit-scrollbar {
  display: none;
}

// Country code container
.style-prefix {
  font-size: $medium-font-size;
  border-bottom: 1px solid rgba(74, 74, 74, 0.25);
}
.style-country-code-container {
  height: 55px;
  display: flex;
  align-items: center;
  width: 98%;
}

.style-item-selected {
  background-color: $text-blue;
}

.style-modal-header {
  position: relative;
}

.style-close-icon {
  position: absolute;
  right: 10px;
  top: 10px;
  font-size: 25px;
  -webkit-text-stroke: 1px;
}
</style>
